import React, { Component } from 'react';

import { Card, Callout, Grid, Icon, Text } from 'core/components';
import { inject, observer } from 'mobx-react';
import { ReactComponent as CPUIcon } from 'app/assets/icons/cpu_icon.svg';
import LightweightDataViewWrapper from 'app/components/dataviews/LightweightDataViewWrapper';
import { getHealthStateFromValue } from 'app/views/hybrid/utils/health';
import DeviceMetricsDetailCharts from 'app/views/hybrid/maps/components/deviceMetrics/DeviceMetricsDetailCharts';

// Legacy flow-based query for CPU metrics
const createFlowQuery = (deviceName) => ({
  all_devices: false,
  device_name: [deviceName],
  fastData: 'Full',
  metric: ['ktappprotocol__snmp_device_metrics__STR01'], // component
  aggregateTypes: ['avg_ktappprotocol__snmp_device_metrics__INT64_00'], // utilization
  lookback_seconds: 86400,
  show_overlay: false,
  show_total_overlay: false
});

const createKmetricsQuery = (deviceId) => {
  // Early return if deviceId is not available - invalid query
  if (!deviceId) {
    return null;
  }

  const filterValue = String(deviceId);

  const query = {
    use_kmetrics: true,
    show_overlay: false,
    show_total_overlay: false,
    kmetrics: {
      measurement: '/components/cpu/utilization', // Component breakdown
      dimensions: ['device_id', 'index', 'name', 'type'], // Component dimensions
      metrics: [
        { name: 'instant', type: 'gauge' } // Correct metric name
      ],
      range: {
        lookback: 'PT3600S'
      },
      rollups: {
        avg_instant: {
          metric: 'instant',
          aggregate: 'avg'
        }
      },
      sort: [{ name: 'device_id', direction: 'asc' }],
      limit: 100,
      includeTimeseries: 20,
      filters: {
        connector: 'All',
        filterGroups: [
          {
            connector: 'All',
            filters: [
              {
                filterField: 'device_id',
                operator: '=',
                filterValue
              }
            ]
          }
        ]
      },
      viz: {
        metric: 'instant' // Match the metric name
      }
    }
  };
  return query;
};

@inject('$devices')
@observer
export default class CPUChart extends Component {
  state = (() => {
    const { $devices, deviceName } = this.props;
    return {
      device: $devices.byName(deviceName),
      kmetricsFailed: false
    };
  })();

  componentDidUpdate(prevProps) {
    const { $devices, deviceName } = this.props;
    const prevDevice = prevProps.$devices?.byName(prevProps.deviceName);
    const currentDevice = $devices.byName(deviceName);

    const prevDeviceId = prevDevice?.get('id');
    const currentDeviceId = currentDevice?.get('id');

    // Only update if device IDs are actually different
    if (currentDeviceId !== prevDeviceId && (currentDeviceId || prevDeviceId)) {
      this.setState({ device: currentDevice, kmetricsFailed: false });
    }
  }

  get flowQuery() {
    const { deviceName } = this.props;
    return createFlowQuery(deviceName);
  }

  get kmetricsQuery() {
    const { device } = this.state;
    if (!device) {
      return null;
    }
    const deviceId = device.get('id');
    // createKmetricsQuery will return null if deviceId is invalid
    return createKmetricsQuery(deviceId);
  }

  get query() {
    const { kmetricsFailed } = this.state;
    // If kmetrics has failed or is unavailable, use flow query instead
    const { kmetricsQuery } = this;
    return kmetricsFailed || !kmetricsQuery ? this.flowQuery : kmetricsQuery;
  }

  get loadingState() {
    return <Card height={100} mb={1} showSkeleton />;
  }

  get emptyState() {
    return (
      <Callout>
        <Icon icon={CPUIcon} />
        <Text ml={1}>CPU Data Unavailable</Text>
      </Callout>
    );
  }

  getFlowResults = (results) => {
    const rawRows = results.getRawDataRows();

    return rawRows.map((model) => {
      const value = model.get('avg_ktappprotocol__snmp_device_metrics__INT64_00');
      const healthState = getHealthStateFromValue({ type: 'cpu', value });
      let color = 'primary';
      let headingProps = {};

      if (healthState.state !== 'GOOD') {
        color = healthState.color;
        headingProps = { color: healthState.color, fontWeight: 'bold' };
      }

      const flowRawData = model.get('rawData').f_avg_int64_00.flow;

      return {
        key: model.get('key'),
        value,
        lastDatapoint: model.get('f_avg_int64_00__k_last'),
        rawData: flowRawData,
        color,
        headingProps
      };
    });
  };

  getKmetricsResults = (results) => {
    // For kmetrics, use results.models directly instead of getRawDataRows()
    const models = results.models || [];

    return models.map((model) => {
      const value = model.get('avg_instant'); // Average CPU utilization
      // For kmetrics, timeseries data is in the 'timeseries' field
      // Format: [[timestamp_ms, utilization_value], ...]
      let rawData = model.get('timeseries') || [];

      // Transform rawData to ensure it's a simple array of [x, y] pairs
      // This prevents Highcharts from treating each point as an object with marker properties
      rawData = rawData.map((point) => {
        if (Array.isArray(point)) {
          // Create a new array to avoid any attached properties
          return {
            x: Number(point[0]),
            y: Number(point[1]),
            marker: { enabled: false }
          };
        }
        // If it's an object, extract x and y
        return {
          x: Number(point.x),
          y: Number(point.y),
          marker: { enabled: false }
        };
      });

      // Use the 'name' dimension as the component name (FPC, Routing Engine, etc.)
      const componentName = model.get('name');

      const healthState = getHealthStateFromValue({ type: 'cpu', value });
      let color = 'primary';
      let headingProps = {};

      if (healthState.state !== 'GOOD') {
        color = healthState.color;
        headingProps = { color: healthState.color, fontWeight: 'bold' };
      }

      return {
        key: componentName || model.get('key'),
        value,
        lastDatapoint: value,
        rawData,
        color,
        headingProps
      };
    });
  };

  handleQueryComplete = ({ results, fullyLoaded }) => {
    const { kmetricsFailed } = this.state;
    const modelCount = results?.models?.length || 0;

    // If kmetrics returned no data and is fully loaded, mark it as failed
    // This will cause the query getter to switch to flow query on next render
    if (modelCount === 0 && fullyLoaded && !kmetricsFailed) {
      this.setState({ kmetricsFailed: true });
    }
  };

  getResults = (results) => {
    const { kmetricsFailed } = this.state;
    // Use results processor based on which query mode is active
    return kmetricsFailed ? this.getFlowResults(results) : this.getKmetricsResults(results);
  };

  render() {
    return (
      <LightweightDataViewWrapper query={this.query} onQueryComplete={this.handleQueryComplete}>
        {({ loading, results }) => {
          if (loading) {
            return this.loadingState;
          }

          const data = this.getResults(results);

          if (data.length > 0) {
            return (
              <Grid gridTemplateColumns="repeat(auto-fit, minmax(350px, 1fr))" gridGap={4} gridRowGap={2}>
                <DeviceMetricsDetailCharts data={data} title="Average CPU Utilization" icon={CPUIcon} />
              </Grid>
            );
          }

          return this.emptyState;
        }}
      </LightweightDataViewWrapper>
    );
  }
}
